import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { IonModal, ModalController } from '@ionic/angular';
import {
    AddressResult,
    PoiResult,
    ApplicationFacade,
    PoiAddressResult,
    FavoriteLocation,
    FavoritesFacade,
    JourneyDirection,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    OtpUtil,
    SearchResult,
    SearchService,
    SearchUtil,
    Stop,
    StopRouteResult,
    TransitNetworkPartType,
    OtpRecentSearchUtilService
} from '@ridango/components';
import { SelectFromMapModalComponent } from '../select-from-map-modal/select-from-map-modal.component';
import { combineLatest, forkJoin, Subject } from 'rxjs';
import { LatLng } from 'leaflet';
import { ThemeFeature } from '@ridango/core/lib/enums/features.enum';
import { filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { BaseComponent } from '@ridango/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-journey-planner-search-modal',
    templateUrl: './journey-planner-search-modal.component.html',
    styleUrls: ['./journey-planner-search-modal.component.scss']
})
export class JourneyPlannerSearchModalComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() modalType: JourneyDirection;

    @ViewChild(IonModal) modal: IonModal;
    @ViewChild('input') input;

    themeFeature = ThemeFeature;
    assetsPath$ = this.applicationFacade.assetsPath$;
    searchText: string;
    location$ = this.openTripPlannerFacade.location$;
    searchPhrase$: Subject<string> = new Subject<string>();

    modalTypeEnum = JourneyDirection;
    searchResultItems: SearchResult;
    sortedResult: SearchResult;
    searchLoading: boolean;
    favoriteLocations$ = this.favoritesFacade.favoriteLocations$;
    favoriteStops: Stop[];
    recentSearchItems: (AddressResult | StopRouteResult | PoiAddressResult)[];
    searchValue = '';
    lastSearchValue = '';
    search$ = this.openTripPlannerFacade.search$;
    currentSearchPhrase = '';

    readonly TransitNetworkPartType = TransitNetworkPartType;

    private readonly searchUtil = SearchUtil;
    private readonly loadMoreAmount = this.searchUtil.getSearchResultsLimit(this.env);
    private readonly RECENT_KEY = 'journey-planner';
    private readonly resultTypes = {
        [TransitNetworkPartType.Stop]: 'stopRoutes',
        [TransitNetworkPartType.Location]: 'addresses',
        [TransitNetworkPartType.POI]: 'poi'
    };

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(DOCUMENT) private document: Document,
        private applicationFacade: ApplicationFacade,
        private renderer: Renderer2,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private openTripPlannerService: OpenTripPlannerService,
        private modalCtrl: ModalController,
        public searchService: SearchService,
        private translate: TranslateService,
        private favoritesFacade: FavoritesFacade,
        private readonly route: ActivatedRoute,
        private readonly otpRecentSearchUtilService: OtpRecentSearchUtilService
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.env.JOURNEY_PLANNER?.SHOW_FAVORITES_STOPS_AND_LOCATIONS) {
            this.favoritesFacade.getFavoriteStops();
            this.favoritesFacade.getFavoriteLocations();
            combineLatest(this.favoritesFacade.favoriteStops$, this.openTripPlannerFacade.stops$)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(([favorites, stops]) => {
                    if (!stops?.length || !favorites?.length) {
                        return;
                    }

                    this.favoriteStops = stops
                        .filter((stop) => !!favorites.find((favorite) => favorite.stopId === stop.id))
                        .map((stop) => {
                            const fav = favorites.find((f) => f.stopId === stop.id);

                            return { ...stop, name: fav.name || stop.name };
                        });
                });
        }

        this.initSearch();
        this.initRecentSearch();
        this.watchRecentListModification();
        this.listenSearchPointTick();
    }

    private listenSearchPointTick(): void {
        this.searchService
            .getRoutePointSubject(this.modalType)
            .pipe(
                filter((value) => !!value && !!Object.keys(this.route.snapshot.queryParams).length),
                takeUntil(this.componentDestroyed$),
                tap((item) => {
                    if (!item.value.coordinates) {
                        return;
                    }

                    this.openTripPlannerFacade.setSearchAddress(
                        this.modalType,
                        item.value.name,
                        item.value.coordinates,
                        item.value.useCurrentLocation,
                        false,
                        item.value.municipalityName
                    );
                })
            )
            .subscribe();
    }

    clearRecentSearchItems(): void {
        this.otpRecentSearchUtilService
            .clearRecentSearchItems(this.RECENT_KEY)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.searchService.tickRecentsModification();
                this.recentSearchItems = [];
            });
    }

    watchRecentListModification(): void {
        this.searchService.recentsModification$
            .pipe(
                switchMap(() => this.otpRecentSearchUtilService.getRecentSearchItems(this.RECENT_KEY)),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((items) => {
                this.recentSearchItems = items ?? [];
            });
    }

    isModalOpen(isOpen: boolean): void {
        if (isOpen) {
            this.renderer.addClass(this.document.body, 'overflow-hidden');
        } else {
            this.renderer.removeClass(this.document.body, 'overflow-hidden');
        }
    }

    onDidPresent(): void {
        this.setInputFocus();
    }

    private setInputFocus(): void {
        this.input.setFocus();
    }

    closeModal(): void {
        this.modal.dismiss(null, 'cancel');
        this.searchValue = this.lastSearchValue;
    }

    setCurrentLocation(): void {
        this.location$.pipe(take(1)).subscribe((location) => {
            if (location.coordinates) {
                this.openTripPlannerFacade.setSearchAddress(
                    this.modalType,
                    'CURRENT_LOCATION',
                    location.coordinates,
                    true
                );
                this.closeModal();
            }
        });
    }

    setFavoriteStop(stop: Stop): void {
        if (stop) {
            this.openTripPlannerFacade.setSearchAddress(
                this.modalType,
                stop.name,
                new LatLng(stop.lat, stop.lon),
                false,
                false,
                stop.desc
            );
            this.closeModal();
        }
    }

    setFavoriteLocation(location: FavoriteLocation): void {
        if (location) {
            this.openTripPlannerFacade.setSearchAddress(
                this.modalType,
                location.name,
                new LatLng(location.latitude, location.longitude),
                false
            );
            this.closeModal();
        }
    }

    shouldShowDivider(index: number, hasPreviousResults: boolean): boolean {
        return hasPreviousResults || index > 0;
    }

    onSearchChange(inputValue): void {
        this.searchValue = inputValue;
        this.searchPhrase$.next(inputValue);
    }

    loadMoreItems(type: TransitNetworkPartType): void {
        this.appendNextBatchByType(type);
    }

    handleSearchItemSelect(selectedItem: StopRouteResult | AddressResult | PoiResult) {
        if (this.searchUtil.isStopRouteResult(selectedItem)) {
            this.openTripPlannerService
                .getStop(selectedItem.id)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((stop) => {
                    this.openTripPlannerFacade.setSearchAddress(
                        this.modalType,
                        stop.name,
                        new LatLng(stop.lat, stop.lon),
                        false,
                        false,
                        stop.desc
                    );
                });
        }
        if (this.searchUtil.isAddressResult(selectedItem)) {
            this.openTripPlannerFacade.setSearchAddress(
                this.modalType,
                selectedItem.address || selectedItem.name,
                new LatLng(selectedItem.lat, selectedItem.lon),
                false
            );
        }

        if (this.searchUtil.isPoiAddressResult(selectedItem)) {
            this.openTripPlannerFacade.setSearchAddress(
                this.modalType,
                selectedItem.address || selectedItem.poi_name || selectedItem.name,
                new LatLng(selectedItem.lat, selectedItem.lon),
                false
            );
        }

        if (this.searchUtil.isPoiAddressResult(selectedItem)) {
            this.openTripPlannerFacade.setSearchAddress(
                this.modalType,
                selectedItem.address || selectedItem.name,
                new LatLng(selectedItem.lat, selectedItem.lon),
                false
            );
        }

        if (this.searchUtil.isPOIResult(selectedItem)) {
            this.openTripPlannerFacade.setSearchAddress(
                this.modalType,
                selectedItem.poi_name || selectedItem.name,
                new LatLng(selectedItem.lat, selectedItem.lon),
                false
            );
        }

        forkJoin([
            this.otpRecentSearchUtilService.addToRecentSearch(
                selectedItem,
                this.RECENT_KEY,
                this.env?.BUS_STOPS_PREVIOUS_SEARCHES_LIMIT
            ),
            this.otpRecentSearchUtilService.addToCachedSearch(selectedItem, this.RECENT_KEY)
        ]).subscribe({
            next: () => {
                this.searchService.tickRecentsModification();
            },
            error: (error) => {
                console.error('Failed to save to recents:', error);
            },
            complete: () => {
                this.closeModal();
            }
        });
    }

    loadMoreBtnVisible(type: TransitNetworkPartType): boolean {
        const searchType = this.resultTypes[type];
        const currentLength = this.searchResultItems[searchType]?.length ?? 0;
        const totalLength = this.sortedResult[searchType]?.length ?? 0;

        return this.env.SEARCH_SETTINGS?.SHOW_LOAD_MORE && totalLength - currentLength >= this.loadMoreAmount;
    }

    private appendNextBatchByType(type: TransitNetworkPartType): void {
        const searchType = this.resultTypes[type];
        const currentList = this.searchResultItems[searchType];
        const sourceList = this.sortedResult[searchType];

        if (!sourceList?.length) return;

        const visibleItemsCount = currentList.length;
        if (visibleItemsCount >= sourceList.length) return;

        const nextItems = sourceList.slice(visibleItemsCount, visibleItemsCount + this.loadMoreAmount);

        currentList.push(...nextItems);
    }

    private initSearch() {
        this.searchPhrase$.pipe(takeUntil(this.componentDestroyed$)).subscribe((phrase) => {
            this.currentSearchPhrase = phrase;
        });

        this.searchService.searchLoading
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((loading) => (this.searchLoading = Object.values(loading).some((value) => value)));

        combineLatest(
            this.searchService.InitSearch(this.searchPhrase$, 10, TransitNetworkPartType.Stop),
            this.searchPhrase$
        )
            .pipe(
                tap(() => {
                    this.searchService.updateSearchResultsAnnouncementMessage(this.searchResultItems);
                }),
                switchMap(([result, searchPhrase]) =>
                    this.otpRecentSearchUtilService
                        .getCachedSearchItems(this.RECENT_KEY)
                        .pipe(map((cachedSearchItems) => ({ result, searchPhrase, cachedSearchItems })))
                ),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(({ result, searchPhrase, cachedSearchItems }) => {
                this.searchText = searchPhrase;
                this.sortedResult = this.searchUtil.sortArraysByRecentSearchItems(
                    result,
                    cachedSearchItems,
                    searchPhrase
                );

                return (this.searchResultItems = this.env.SEARCH_SETTINGS
                    ? this.searchUtil.limitSearchResults(this.sortedResult, this.env)
                    : this.sortedResult);
            });

        this.search$.pipe(takeUntil(this.componentDestroyed$)).subscribe((result) => {
            let displayValue = '';

            if (!result?.[this.modalType]?.useCurrentLocation && !result?.[this.modalType]?.useLocationFromMap) {
                const name = result?.[this.modalType]?.name;
                if (name === 'LOCATION_FROM_MAP') {
                    displayValue = this.translate.instant('LOCATION_FROM_MAP');
                } else if (name === 'CURRENT_LOCATION') {
                    displayValue = this.translate.instant('CURRENT_LOCATION');
                } else {
                    displayValue = name || '';
                }
            }

            this.searchValue = displayValue;
            this.lastSearchValue = this.searchValue;
        });
    }

    private initRecentSearch() {
        this.searchPhrase$
            .pipe(
                startWith(''),
                filter((val) => !val),
                switchMap(() => this.otpRecentSearchUtilService.getRecentSearchItems(this.RECENT_KEY)),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((items) => {
                this.recentSearchItems = items ?? [];
            });
    }

    async openModal() {
        const modal = await this.modalCtrl.create({
            component: SelectFromMapModalComponent,
            cssClass: 'select-from-map-modal',
            componentProps: { modalType: this.modalType }
        });

        modal.present();

        const { data } = await modal.onWillDismiss();

        if (data?.name) {
            this.openTripPlannerFacade.setSearchAddress(
                this.modalType,
                data.name,
                data.positionBus,
                false,
                false,
                data.desc
            );
        } else if (data) {
            this.openTripPlannerFacade.setSearchAddress(this.modalType, 'LOCATION_FROM_MAP', data, false, true);
        }
        this.closeModal();
    }

    isMobileDevice(): boolean {
        return OtpUtil.isMobileDevice();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
